/**
 * "When does each one end" — a bar per commitment along a shared time axis.
 *
 * This is the view the whole feature was asked for. A list can tell you the
 * last instalment is in July 2028; only a shared axis tells you that the car
 * lease and the loan both end within three months of each other, and that the
 * mortgage runs a decade past both.
 *
 * Open-ended commitments (salary, subscriptions) have no end. They are drawn
 * running off the right edge with a fade rather than being given an invented
 * finish line — the rolling horizon in the database is an implementation
 * detail, not a fact about the world.
 */
import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { colorClasses } from '@/lib/colors';
import { formatDate, formatMoney } from '@/lib/format';
import type { Commitment } from '@/lib/types';

interface Row {
  c: Commitment;
  startPct: number;
  widthPct: number;
  openEnded: boolean;
}

export function CommitmentTimeline({
  commitments,
  baseCurrency,
  baseDecimals,
  onSelect,
}: {
  commitments: Commitment[];
  baseCurrency: string;
  baseDecimals: number;
  onSelect?: (c: Commitment) => void;
}): JSX.Element | null {
  const { rows, axis, todayPct } = useMemo(() => {
    const relevant = commitments.filter((c) => !c.archived && c.remainingCount > 0);
    if (relevant.length === 0) return { rows: [], axis: [], todayPct: 0 };

    const now = Date.now();
    const ends = relevant
      .map((c) => (c.lastDueDate ? new Date(c.lastDueDate).getTime() : null))
      .filter((t): t is number => t != null);

    // The axis starts today: what already happened is not what this view is
    // about. It ends at the last finite commitment, with at least two years
    // shown so a single short obligation does not fill the whole width.
    const start = now;
    const minEnd = now + 2 * 365 * 86_400_000;
    const end = Math.max(minEnd, ...(ends.length ? ends : [minEnd]));
    const span = end - start || 1;

    const pct = (t: number) => ((t - start) / span) * 100;

    const sorted = [...relevant].sort((a, b) => {
      // Finite first, soonest ending first; open-ended last.
      if (!a.lastDueDate && !b.lastDueDate) return a.name.localeCompare(b.name, 'bg');
      if (!a.lastDueDate) return 1;
      if (!b.lastDueDate) return -1;
      return a.lastDueDate.localeCompare(b.lastDueDate);
    });

    const rows: Row[] = sorted.map((c) => {
      const openEnded = c.lastDueDate == null;
      const endT = openEnded ? end : new Date(c.lastDueDate!).getTime();
      const startT = c.nextDueDate ? Math.max(now, new Date(c.nextDueDate).getTime()) : now;
      const startPct = Math.max(0, Math.min(100, pct(startT)));
      const widthPct = Math.max(1.5, Math.min(100 - startPct, pct(endT) - startPct));
      return { c, startPct, widthPct, openEnded };
    });

    // Year ticks.
    const axis: Array<{ label: string; pct: number }> = [];
    const firstYear = new Date(start).getUTCFullYear();
    const lastYear = new Date(end).getUTCFullYear();
    for (let y = firstYear; y <= lastYear; y++) {
      const t = Date.UTC(y, 0, 1);
      if (t < start) continue;
      axis.push({ label: String(y), pct: pct(t) });
    }

    return { rows, axis, todayPct: 0 };
  }, [commitments]);

  if (rows.length === 0) return null;

  return (
    <div className="space-y-3">
      {/* Year axis */}
      <div className="relative h-4 sm:ml-44">
        {axis.map((a) => (
          <span
            key={a.label}
            className="absolute -translate-x-1/2 text-[10px] text-muted-foreground"
            style={{ left: `${a.pct}%` }}
          >
            {a.label}
          </span>
        ))}
      </div>

      <div className="space-y-2">
        {rows.map(({ c, startPct, widthPct, openEnded }) => {
          const colors = colorClasses(c.color);
          return (
            <div key={c.id} className="sm:flex sm:items-center sm:gap-3">
              <button
                type="button"
                onClick={() => onSelect?.(c)}
                className="mb-1 flex w-full min-w-0 items-baseline gap-2 text-left text-sm hover:underline sm:mb-0 sm:w-44 sm:shrink-0"
              >
                <span className={cn('h-2 w-2 shrink-0 rounded-full', colors.bg)} />
                <span className="truncate">{c.name}</span>
              </button>

              <div className="relative h-7 flex-1 overflow-hidden rounded bg-muted/50">
                {/* Year gridlines, so a bar's end can be read against the axis. */}
                {axis.map((a) => (
                  <span
                    key={a.label}
                    className="absolute inset-y-0 w-px bg-border"
                    style={{ left: `${a.pct}%` }}
                    aria-hidden
                  />
                ))}

                <div
                  className={cn(
                    'absolute inset-y-1 rounded',
                    colors.bg,
                    openEnded && 'rounded-r-none',
                  )}
                  style={{
                    left: `${startPct}%`,
                    width: `${widthPct}%`,
                    // Open-ended: fade out instead of drawing a false end.
                    ...(openEnded
                      ? { maskImage: 'linear-gradient(to right, black 70%, transparent 100%)',
                          WebkitMaskImage: 'linear-gradient(to right, black 70%, transparent 100%)' }
                      : {}),
                  }}
                />

                <span className="absolute inset-y-0 right-2 flex items-center text-[11px] text-muted-foreground">
                  {openEnded ? 'безсрочно' : c.lastDueDate ? formatDate(c.lastDueDate) : ''}
                </span>

                {/* "Today" marker at the very left edge of the axis. */}
                <span
                  className="absolute inset-y-0 w-0.5 bg-foreground/60"
                  style={{ left: `${todayPct}%` }}
                  aria-hidden
                />
              </div>

              <div className="mt-0.5 flex justify-between text-xs text-muted-foreground sm:mt-0 sm:w-36 sm:shrink-0 sm:justify-end">
                {openEnded ? (
                  <span>{formatMoney(c.amountMinor, c.currency, c.decimals)}</span>
                ) : (
                  <span>
                    {c.remainingCount} × {formatMoney(c.amountMinor, c.currency, c.decimals)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground sm:ml-44">
        Общо остава:{' '}
        <span className="tabular font-medium text-foreground">
          {formatMoney(
            commitments
              .filter((c) => !c.archived && c.direction === 'EXPENSE')
              .reduce((n, c) => n + c.remainingMinor, 0),
            baseCurrency,
            baseDecimals,
          )}
        </span>{' '}
        — включва лихвата в бъдещите вноски, не е остатък по главница.
      </p>
    </div>
  );
}
